import { useState } from 'react';
import type { Game } from '../../mocks/games';
import type { FolderType } from '../../hooks/usePocket';
import FolderSelectModal from './FolderSelectModal';

interface GameCardProps {
  game: Game;
  isInPocket: boolean;
  currentFolder?: string;
  onAddToPocket: (gameId: string, folder: FolderType) => void;
  onRemoveFromPocket: (gameId: string) => void;
}

export default function GameCard({
  game,
  isInPocket,
  currentFolder,
  onAddToPocket,
  onRemoveFromPocket
}: GameCardProps) {
  const [showFolderModal, setShowFolderModal] = useState(false);

  const ttplayConfig = {
    '10s': { label: '즉시', icon: '⚡', color: 'bg-emerald-500' },
    'loading': { label: '로딩', icon: '⏳', color: 'bg-amber-500' },
    'login': { label: '로그인', icon: '🔐', color: 'bg-rose-500' }
  };

  const deviceConfig = {
    pc: { label: 'PC', icon: '💻' },
    mobile: { label: '모바일', icon: '📱' },
    both: { label: '둘다', icon: '🎮' }
  };

  const ttplayInfo = ttplayConfig[game.ttplay];
  const deviceInfo = deviceConfig[game.device];

  // URL에서 도메인 추출
  const getDomain = (url: string) => {
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      return domain;
    } catch {
      return '';
    }
  };

  const handlePocketClick = () => {
    if (isInPocket) {
      onRemoveFromPocket(game.id);
    } else {
      setShowFolderModal(true);
    }
  };

  const handleFolderSelect = (folder: FolderType) => {
    onAddToPocket(game.id, folder);
    setShowFolderModal(false);
  };

  return (
    <>
      <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300 overflow-hidden border border-gray-700 hover:border-cyan-500/50">
        <div className="relative w-full h-48 overflow-hidden bg-gray-950">
          <img 
            src={game.screenshot} 
            alt={game.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
        </div>
        
        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                {game.title}
              </h3>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-xs text-gray-500">외부 사이트에서 플레이</span>
                <i className="ri-external-link-line text-xs text-gray-500"></i>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                {game.why}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white ${ttplayInfo.color} shadow-sm`}>
              <span>{ttplayInfo.icon}</span>
              {ttplayInfo.label}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-700 text-gray-200">
              <span>{deviceInfo.icon}</span>
              {deviceInfo.label}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-800 text-gray-400 border border-gray-700">
              <i className="ri-global-line text-xs"></i>
              {getDomain(game.playUrl)}
            </span>
          </div>

          <div className="flex gap-2">
            <a
              href={game.playUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2.5 rounded-xl font-semibold text-sm hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg hover:shadow-cyan-500/30 whitespace-nowrap text-center cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <span>플레이</span>
              <i className="ri-external-link-line text-sm"></i>
            </a>
            <button
              onClick={handlePocketClick}
              className={`px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer ${
                isInPocket
                  ? 'bg-yellow-500 text-yellow-900 hover:bg-yellow-400'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              title={isInPocket ? '주머니에서 제거' : '주머니에 추가'}
            >
              {isInPocket ? '★' : '☆'}
            </button>
          </div>

          {isInPocket && currentFolder && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                <span>📁</span>
                {currentFolder}
              </span>
            </div>
          )}
        </div>
      </div>

      {showFolderModal && (
        <FolderSelectModal
          onSelect={handleFolderSelect}
          onClose={() => setShowFolderModal(false)}
        />
      )}
    </>
  );
}
